import { useMemo, useState } from "react";
import { ROW_LAYOUTS, withDefaultPipe, type BlockDefinition, type DataSource, type FieldSchema, type LibraryItem, type Node } from "@broadpaper/core";
import { useSession, useUi, useFeatures, useOptions, useTemplate, useEditorState, useRegistryVersion, nodeLabel, nodeDetail } from "../state/context.js";
import type { EditorActions } from "../state/actions.js";
import type { LeftTab } from "../state/session.js";
import { beginDrag } from "../dnd/drag-manager.js";
import { Icon, FIELD_TYPE_ICONS } from "../ui/icons.js";
import { Button, TextInput, EmptyState } from "../ui/primitives.js";
import { AiPanel } from "./AiPanel.js";

const TABS: Array<{ id: LeftTab; label: string; icon: string; feature?: "library" | "layers" | "dataPanel"; needsAi?: boolean }> = [
  { id: "blocks", label: "Blocks", icon: "blocks" },
  { id: "layouts", label: "Layout", icon: "layout-grid" },
  { id: "data", label: "Data", icon: "database", feature: "dataPanel" },
  { id: "layers", label: "Layers", icon: "layers", feature: "layers" },
  { id: "library", label: "Library", icon: "book-marked", feature: "library" },
  // Only when the host passed a provider: without one there is nothing it could
  // do, and a tab that only ever says "not available" is clutter.
  { id: "ai", label: "AI", icon: "sparkles", needsAi: true }
];

export function LeftPanel({ actions }: { actions: EditorActions }) {
  const session = useSession();
  const tab = useUi((u) => u.leftTab);
  const features = useFeatures();
  const hasAi = !!useOptions().ai;
  const tabs = TABS.filter((t) => (!t.feature || features[t.feature]) && (!t.needsAi || hasAi));
  return (
    <aside className="bpe-left" aria-label="Insert and navigate">
      {/* Every tab keeps its name. Showing the label only on the selected tab
          made the other four an icon quiz — the one thing you did not need
          spelled out was the panel already in front of you. */}
      <div className="bpe-tabs" role="tablist" aria-label="Panels">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`bpe-tab-${t.id}`}
            className="bpe-tab"
            aria-selected={tab === t.id}
            aria-controls="bpe-left-panel"
            tabIndex={tab === t.id ? 0 : -1}
            onKeyDown={(e) => {
              const delta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
              if (!delta) return;
              e.preventDefault();
              const next = tabs[(tabs.findIndex((x) => x.id === tab) + delta + tabs.length) % tabs.length]!;
              session.ui.patch({ leftTab: next.id });
              session.doc.getElementById(`bpe-tab-${next.id}`)?.focus();
            }}
            onClick={() => session.ui.patch({ leftTab: t.id })}
          >
            <Icon name={t.icon} />
            <span>{t.label}</span>
          </button>
        ))}
      </div>
      <div className="bpe-left__body" role="tabpanel" id="bpe-left-panel" aria-labelledby={`bpe-tab-${tab}`}>
        {tab === "blocks" ? <BlocksPanel actions={actions} /> : null}
        {tab === "layouts" ? <LayoutsPanel actions={actions} /> : null}
        {tab === "data" ? <DataPanel actions={actions} /> : null}
        {tab === "layers" ? <LayersPanel actions={actions} /> : null}
        {tab === "library" ? <LibraryPanel actions={actions} /> : null}
        {tab === "ai" && hasAi ? <AiPanel /> : null}
      </div>
    </aside>
  );
}

/* --------------------------------- blocks --------------------------------- */

const CATEGORY_ORDER: Array<{ id: BlockDefinition["category"]; label: string }> = [
  { id: "content", label: "Content" },
  { id: "data", label: "Data" },
  { id: "layout", label: "Layout" },
  { id: "visualisation", label: "Charts" },
  { id: "custom", label: "Custom blocks" }
];

function BlocksPanel({ actions }: { actions: EditorActions }) {
  const session = useSession();
  const features = useFeatures();
  useRegistryVersion();
  const [q, setQ] = useState("");
  const defs = session.registry.palette().filter((d) => features.customBlocks || d.category !== "custom");
  const filtered = q.trim() ? defs.filter((d) => [d.name, d.description ?? "", ...(d.keywords ?? [])].join(" ").toLowerCase().includes(q.toLowerCase())) : defs;
  return (
    <div className="bpe-palette">
      <div className="bpe-search">
        <Icon name="search" />
        <TextInput value={q} onChange={setQ} placeholder="Search blocks…" aria-label="Search blocks" />
      </div>
      {CATEGORY_ORDER.map((cat) => {
        const items = filtered.filter((d) => d.category === cat.id);
        if (!items.length) return null;
        return (
          <div key={cat.id} className="bpe-palette__group">
            <div className="bpe-palette__title">{cat.label}</div>
            <div className="bpe-palette__grid">
              {items.map((d) => {
                const tier = d.license && d.license !== "community" ? d.license : null;
                // The badge used to be a CSS ::after — invisible to a screen
                // reader and impossible to explain. Whether the tier is enabled
                // is something the registry knows, so say which it is rather
                // than leaving "PRO" to be read as "you cannot have this".
                const enabled = !tier || session.registry.isTierEnabled(d.license);
                return (
                  <button
                    key={d.type}
                    type="button"
                    className={`bpe-palette__item ${tier ? "bpe-palette__item--pro" : ""} ${enabled ? "" : "bpe-palette__item--locked"}`}
                    title={`${d.description ?? d.name}\nClick to insert, or drag onto the page.${tier ? `\n\n${tierNote(tier, enabled)}` : ""}`}
                    disabled={!enabled}
                    onPointerDown={(e) => enabled && beginDrag(session, e, { kind: "new", payload: d.type, label: d.name, icon: d.icon })}
                    onClick={() => (d.type === "row" ? actions.insertRow([6, 6]) : actions.insertBlock(d.type))}
                    onKeyDown={(e) => e.key === "Enter" && (d.type === "row" ? actions.insertRow([6, 6]) : actions.insertBlock(d.type))}
                  >
                    <Icon name={d.icon ?? "box"} size={16} />
                    <span className="bpe-palette__item-name">{d.name}</span>
                    {tier ? <span className="bpe-palette__tier" title={tierNote(tier, enabled)} aria-label={tierNote(tier, enabled)}>{tier === "pro" ? "PRO" : "ENT"}</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      {filtered.length === 0 ? <EmptyState icon="search" title="No blocks match">Try a different search.</EmptyState> : null}
    </div>
  );
}

/**
 * What a tier badge means here, which depends on what the host has turned on.
 * Claiming a block "requires a licence" while it inserts and works perfectly
 * well in the editor in front of you is a licensing claim the editor is in no
 * position to make.
 */
function tierNote(tier: string, enabled: boolean): string {
  const name = tier === "pro" ? "Pro" : "Enterprise";
  return enabled
    ? `In the ${name} tier, and enabled in this editor — insert it and it works.`
    : `In the ${name} tier, which this editor has not enabled.`;
}

/* --------------------------------- layouts -------------------------------- */

function LayoutsPanel({ actions }: { actions: EditorActions }) {
  const session = useSession();
  const template = useTemplate();
  const selection = useEditorState((s) => s.selection);
  return (
    <div className="bpe-palette">
      <div className="bpe-palette__group">
        <div className="bpe-palette__title">Columns</div>
        <div className="bpe-layouts__grid">
          {ROW_LAYOUTS.map((l) => (
            <button key={l.id} type="button" className="bpe-layout-item" title="Click to insert, or drag onto the page" onPointerDown={(e) => beginDrag(session, e, { kind: "row", payload: l.layout, label: l.label, icon: "columns-3" })} onClick={() => actions.insertRow(l.layout)}>
              <div className="bpe-layout-item__cols" style={{ gridTemplateColumns: l.layout.map((c) => `${c}fr`).join(" ") }}>
                {l.layout.map((_, i) => <div key={i} className="bpe-layout-item__col" />)}
              </div>
              <span className="bpe-layout-item__name">{l.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="bpe-palette__group">
        <div className="bpe-palette__title">Sections</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Button icon="plus" onClick={() => actions.addSection("body")}>Add section at end</Button>
          {selection[0] ? <Button icon="corner-down-right" onClick={() => { const e = session.store.index().get(selection[0]!); actions.addSection("body", e ? e.path[0]! + 1 : undefined); }}>Add section after selection</Button> : null}
          <Button icon="scissors" onClick={() => actions.insertBlock("pageBreak")}>Insert page break</Button>
        </div>
        <div className="bpe-field__hint" style={{ marginTop: 8 }}>{template.body.length} section{template.body.length === 1 ? "" : "s"} in the body. Sections stack vertically and can be reordered from the Layers panel.</div>
      </div>
      <div className="bpe-palette__group">
        <div className="bpe-palette__title">Header & footer</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Button icon="panel-top" onClick={() => { if (template.header.default.length === 0) actions.addSection("header"); else session.store.select([template.header.default[0]!.id]); }}>Edit header</Button>
          <Button icon="panel-top" onClick={() => { if (template.footer.default.length === 0) actions.addSection("footer"); else session.store.select([template.footer.default[0]!.id]); }}>Edit footer</Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------- data ---------------------------------- */

/**
 * One row of the data tree, flattened out of the host's schemas.
 *
 * Search runs over this tree rather than over the rendering, so a field buried
 * two levels down is findable by its own name and still arrives with the path
 * that says where it lives. Matching only what a row happened to render was why
 * searching "value" found nothing useful: the fields inside a list were never
 * part of the search at all, and a group was drawn whether or not anything
 * inside it had survived the filter.
 */
interface DataNode {
  key: string;
  /** Full human path, e.g. "Portfolio › Holdings › Value". */
  label: string;
  /** Leaf name, which is what the row shows. */
  name: string;
  type: FieldSchema["type"];
  /** The expression to insert. Absent for rows that are only a heading. */
  expr?: string;
  /** Set for fields inside a list, where the expression needs a row alias. */
  itemOf?: string;
  kind: "field" | "group" | "list";
  /**
   * The field's own schema.
   *
   * Carried so that inserting a binding can write the field's formatter into
   * the expression: the type alone does not say which currency, or which date
   * pattern the host declared.
   */
  schema: FieldSchema;
  children: DataNode[];
}

function buildNodes(fields: Record<string, FieldSchema>, prefix: string, labels: string[]): DataNode[] {
  return Object.entries(fields).map(([key, f]): DataNode => {
    const name = f.label ?? key;
    const expr = prefix + "." + key;
    const label = [...labels, name].join(" › ");
    if (f.type === "object" && f.fields) {
      return { key, label, name, type: f.type, schema: f, kind: "group", children: buildNodes(f.fields, expr, [...labels, name]) };
    }
    if (f.type === "array" && f.itemSchema?.type === "object" && f.itemSchema.fields) {
      // A list's own expression is what a table or repeater binds to; the fields
      // inside it are addressed through that block's row alias, so they are
      // listed and searchable but not insertable on their own.
      const children = Object.entries(f.itemSchema.fields).map(([k2, f2]): DataNode => ({
        key: k2,
        label: [...labels, name, f2.label ?? k2].join(" › "),
        name: f2.label ?? k2,
        type: f2.type,
        schema: f2,
        itemOf: name,
        kind: "field",
        children: []
      }));
      return { key, label, name, type: f.type, schema: f, expr, kind: "list", children };
    }
    return { key, label, name, type: f.type, schema: f, expr, kind: "field", children: [] };
  });
}

/** Keeps every node that matches, and every ancestor of one that does. */
function filterNodes(nodes: DataNode[], q: string): DataNode[] {
  if (!q) return nodes;
  const out: DataNode[] = [];
  for (const n of nodes) {
    const self = n.label.toLowerCase().includes(q) || (n.expr ?? "").toLowerCase().includes(q);
    const children = filterNodes(n.children, q);
    // A group that matches on its own name keeps all of its children, so
    // searching "holdings" shows what is in it rather than an empty folder.
    if (self) out.push({ ...n, children: children.length ? children : n.children });
    else if (children.length) out.push({ ...n, children });
  }
  return out;
}

function DataPanel({ actions }: { actions: EditorActions }) {
  const session = useSession();
  const [q, setQ] = useState("");
  const sources = session.dataSources;
  const query = q.trim().toLowerCase();
  const trees = useMemo(() => sources.map((s) => ({ source: s, nodes: filterNodes(buildNodes(s.schema, s.id, [s.label]), query) })), [sources, query]);
  if (sources.length === 0) return <EmptyState icon="database" title="No data sources">Pass <code>dataSources</code> to the designer to bind fields.</EmptyState>;
  const found = trees.reduce((a, t) => a + t.nodes.length, 0);
  return (
    <div>
      <div className="bpe-search">
        <Icon name="search" />
        <TextInput value={q} onChange={setQ} placeholder="Search fields…" aria-label="Search fields" />
      </div>
      <div className="bpe-data-tree">
        {/* A source with nothing matching is not a folder worth showing. */}
        {trees.map(({ source, nodes }) => (nodes.length ? <DataSourceTree key={source.id} source={source} nodes={nodes} searching={!!query} actions={actions} /> : null))}
        {query && found === 0 ? <EmptyState icon="search" title="No fields match">Try part of a field or source name, such as “value” or “holdings”.</EmptyState> : null}
        <div className="bpe-field__hint" style={{ padding: "8px 6px" }}>Drag a field onto the page to add it, or drop it into a text block to insert a token. Lists become tables.</div>
      </div>
    </div>
  );
}

function DataSourceTree({ source, nodes, searching, actions }: { source: DataSource; nodes: DataNode[]; searching: boolean; actions: EditorActions }) {
  const [open, setOpen] = useState(true);
  // While searching, a collapsed source would hide the very row you searched for.
  const expanded = open || searching;
  return (
    <div className="bpe-data-source">
      <button type="button" className="bpe-data-source__head" aria-expanded={expanded} onClick={() => setOpen(!open)}>
        <Icon name={expanded ? "folder-open" : "folder"} />
        <span className="bpe-truncate">{source.label}</span>
        <span className="bpe-data-field__type">{source.id}</span>
      </button>
      {expanded ? <FieldList nodes={nodes} actions={actions} /> : null}
    </div>
  );
}

function FieldList({ nodes, actions, depth = 0 }: { nodes: DataNode[]; actions: EditorActions; depth?: number }) {
  const session = useSession();
  return (
    <div className={depth > 0 ? "bpe-data-children" : undefined}>
      {nodes.map((n) => {
        if (n.kind === "group") {
          return (
            <div key={n.key}>
              <div className="bpe-data-field bpe-data-field--group" title={n.label}>
                <Icon name="folder" className="bpe-data-field__icon" />
                <span className="bpe-truncate">{n.name}</span>
              </div>
              <FieldList nodes={n.children} actions={actions} depth={depth + 1} />
            </div>
          );
        }
        if (n.itemOf) {
          return (
            <div key={n.key} className="bpe-data-field bpe-data-field--group" title={n.label + "\nAvailable inside a repeater or table over " + n.itemOf}>
              <Icon name={FIELD_TYPE_ICONS[n.type] ?? "box"} className="bpe-data-field__icon" />
              <span className="bpe-truncate">{n.name}</span>
              <span className="bpe-data-field__type">{n.type}</span>
            </div>
          );
        }
        const expr = n.expr!;
        const isList = n.kind === "list";
        // The field's own formatter, written into the expression as it is
        // inserted. Without it a money column reads 482310.4 on the page and a
        // date reads as an ISO timestamp, and the person who dragged the field in
        // has no reason to know a pipe exists.
        const bound = isList ? expr : withDefaultPipe(expr, n.schema);
        return (
          <div key={n.key}>
            <button
              type="button"
              className="bpe-data-field"
              title={n.label + "\n" + expr + "\nClick to insert, or drag onto the page."}
              onPointerDown={(e) => beginDrag(session, e, { kind: "field", payload: { expr, bound, label: n.label, type: n.type, schema: n.schema }, label: n.name, icon: FIELD_TYPE_ICONS[n.type] })}
              onClick={() => actions.insertField({ expr, bound, label: n.label, type: n.type, schema: n.schema })}
            >
              <Icon name={FIELD_TYPE_ICONS[n.type] ?? "box"} className="bpe-data-field__icon" />
              <span className="bpe-truncate">{n.name}</span>
              <span className="bpe-data-field__type">{n.type}</span>
            </button>
            {n.children.length ? <FieldList nodes={n.children} actions={actions} depth={depth + 1} /> : null}
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------- layers --------------------------------- */

function LayersPanel({ actions }: { actions: EditorActions }) {
  const session = useSession();
  const template = useTemplate();
  const selection = useEditorState((s) => s.selection);
  const hoverId = useEditorState((s) => s.hoverId);
  const regions: Array<{ id: string; label: string; nodes: Node[] }> = [
    { id: "header", label: "Header", nodes: template.header.default },
    { id: "body", label: "Body", nodes: template.body },
    { id: "footer", label: "Footer", nodes: template.footer.default }
  ];
  return (
    <div className="bpe-layers" role="tree" aria-label="Document layers">
      {regions.map((r) =>
        r.nodes.length || r.id === "body" ? (
          <div key={r.id}>
            <div className="bpe-palette__title">{r.label}</div>
            {r.nodes.length === 0 ? <div className="bpe-field__hint" style={{ padding: "2px 8px 8px" }}>Empty</div> : null}
            {r.nodes.map((n) => <LayerRow key={n.id} node={n} depth={0} selection={selection} hoverId={hoverId} session={session} actions={actions} />)}
          </div>
        ) : null
      )}
    </div>
  );
}

function LayerRow({ node, depth, selection, hoverId, session, actions }: { node: Node; depth: number; selection: string[]; hoverId: string | null; session: ReturnType<typeof useSession>; actions: EditorActions }) {
  const [open, setOpen] = useState(depth < 2);
  const def = session.registry.get(node.type);
  const kids = node.children ?? [];
  const selected = selection.includes(node.id);
  const label = nodeLabel(session, node);
  const typeName = (def?.name ?? node.type).toLowerCase();
  return (
    <div role="treeitem" aria-selected={selected} aria-expanded={kids.length ? open : undefined}>
      <div
        className={`bpe-layer ${selected ? "bpe-layer--selected" : ""} ${hoverId === node.id ? "bpe-layer--hover" : ""}`}
        // Readable name on the row, the expressions behind it on hover.
        title={nodeDetail(session, node)}
        style={{ paddingLeft: 4 + depth * 14 }}
        onClick={(e) => {
          if (e.shiftKey || e.metaKey || e.ctrlKey) session.store.toggleSelect(node.id);
          else session.store.select([node.id]);
          session.reveal(node.id);
        }}
        onPointerDown={(e) => {
          if (node.locked || node.type === "column") return;
          beginDrag(session, e, { kind: "move", payload: node.id, label: nodeLabel(session, node), icon: def?.icon }, { threshold: 6 });
        }}
        onMouseEnter={() => session.store.hover(node.id)}
        onMouseLeave={() => session.store.hover(null)}
        onDoubleClick={() => def?.canvas?.inlineEdit && session.store.setEditing(node.id)}
      >
        <button type="button" className="bpe-layer__toggle" aria-expanded={open} aria-label={`${open ? "Collapse" : "Expand"} ${label}`} style={{ visibility: kids.length ? "visible" : "hidden" }} onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
          <Icon name="chevron-down" />
        </button>
        <Icon name={def?.icon ?? "box"} className="bpe-layer__icon" />
        <span className="bpe-layer__name">{label}</span>
        {node.visibleWhen ? <Icon name="git-branch" size={11} className="bpe-layer__icon" /> : null}
        {node.locked ? <Icon name="lock" size={11} className="bpe-layer__icon" /> : null}
        {kids.length ? <span className="bpe-layer__meta">{kids.length}</span> : null}
        {node.type === "column" ? null : (
          <span className="bpe-layer__actions">
            <Button size="sm" variant="ghost" icon="arrow-up" tooltip={`Move ${typeName} up`} disabled={!!node.locked} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); session.store.select([node.id]); actions.moveSelection(-1); }} />
            <Button size="sm" variant="ghost" icon="arrow-down" tooltip={`Move ${typeName} down`} disabled={!!node.locked} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); session.store.select([node.id]); actions.moveSelection(1); }} />
            <Button size="sm" variant="ghost" icon="trash" className="bpe-btn--danger" tooltip={`Delete ${typeName}`} disabled={!!node.locked} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); session.store.select([node.id]); actions.deleteSelection(); }} />
          </span>
        )}
      </div>
      {open && kids.length ? <div role="group">{kids.map((c) => <LayerRow key={c.id} node={c} depth={depth + 1} selection={selection} hoverId={hoverId} session={session} actions={actions} />)}</div> : null}
    </div>
  );
}

/* --------------------------------- library -------------------------------- */

function LibraryPanel({ actions }: { actions: EditorActions }) {
  const session = useSession();
  const template = useTemplate();
  const selection = useEditorState((s) => s.selection);
  const hostItems = session.options.get().library ?? [];
  const items: Array<LibraryItem & { source: "host" | "template" }> = [...hostItems.map((i) => ({ ...i, source: "host" as const })), ...(template.library ?? []).map((i) => ({ ...i, source: "template" as const }))];
  const groups = useMemo(() => {
    const m = new Map<string, typeof items>();
    for (const it of items) m.set(it.category ?? (it.source === "host" ? "Shared" : "This report"), [...(m.get(it.category ?? (it.source === "host" ? "Shared" : "This report")) ?? []), it]);
    return [...m.entries()];
  }, [items]);
  return (
    <div style={{ paddingTop: 8 }}>
      {selection.length === 1 ? (
        <div style={{ padding: "0 8px 8px" }}>
          <Button icon="book-marked" style={{ width: "100%" }} onClick={() => { const name = window.prompt("Name this reusable block"); if (name) actions.saveSelectionToLibrary(name); }}>Save selection to library</Button>
        </div>
      ) : null}
      {items.length === 0 ? <EmptyState icon="book-marked" title="Nothing saved yet">Select a block or section and save it here to reuse it. Hosts can also supply shared sections.</EmptyState> : null}
      {groups.map(([g, list]) => (
        <div key={g}>
          <div className="bpe-palette__title" style={{ padding: "8px 10px 6px" }}>{g}</div>
          {list.map((it) => (
            <div key={it.id} className="bpe-library__item" title="Click to insert, or drag onto the page" onPointerDown={(e) => beginDrag(session, e, { kind: "library", payload: it.id, label: it.name, icon: "book-marked" })} onClick={() => { const node = it.node; actions.pasteNodes([node]); }}>
              <Icon name={session.registry.get(it.node.type)?.icon ?? "book-marked"} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="bpe-library__name bpe-truncate">{it.name}</div>
                {it.description ? <div className="bpe-library__desc bpe-truncate">{it.description}</div> : null}
              </div>
              {it.source === "template" ? <Button size="sm" variant="ghost" icon="trash" tooltip="Remove" onClick={(e) => { e.stopPropagation(); session.store.dispatch({ type: "removeLibraryItem", id: it.id }); }} onPointerDown={(e) => e.stopPropagation()} /> : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
